import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { emptyPlan } from '../floorplan/editor';

const PDFJS = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174';
const MAX_SIDE = 3000;      // longest side of a stored photo, in pixels
const MAX_SIDE_PDF = 5000;  // PDFs are line drawings, so keep more detail for zooming in on A1 and A2 sheets
const MAX_SCAN_CHARS = 11 * 1024 * 1024; // the API refuses anything bigger
const SCALES = [1, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 1250, 2500];
const PT_TO_MM = 25.4 / 72;
const ASSUMED_WIDTH_M = 20; // a new drawing starts 20 m wide until someone sets the scale

const r3 = v => Math.round(v * 1000) / 1000;
const todayUK = () => new Date().toLocaleDateString('en-GB', { timeZone: 'Europe/London' });

let pdfjsPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${PDFJS}/pdf.min.js`;
      s.onload = () => { window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS}/pdf.worker.min.js`; resolve(window.pdfjsLib); };
      s.onerror = () => { pdfjsPromise = null; reject(new Error('PDF reader did not load')); };
      document.head.appendChild(s);
    });
  }
  return pdfjsPromise;
};

const isPdf = file => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

// Name the ISO paper size closest to a page, e.g. "A1 (841 x 594 mm)"
const paperName = (wMm, hMm) => {
  const long = Math.max(wMm, hMm), short = Math.min(wMm, hMm);
  const sizes = [['A0', 1189, 841], ['A1', 841, 594], ['A2', 594, 420], ['A3', 420, 297], ['A4', 297, 210]];
  const hit = sizes.find(([, l, sh]) => Math.abs(l - long) < 6 && Math.abs(sh - short) < 6);
  return `${hit ? hit[0] + ' ' : ''}(${Math.round(wMm)} x ${Math.round(hMm)} mm)`;
};

// Render one page of a PDF; the page's paper size comes back too, so the drawing's printed scale can place it exactly
const renderPdfPage = async (pdf, pageNo) => {
  const page = await pdf.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: Math.min(6, MAX_SIDE_PDF / Math.max(base.width, base.height)) });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, wMm: base.width * PT_TO_MM, hMm: base.height * PT_TO_MM };
};

// A photo of a drawing as a canvas
const imageToCanvas = async (file) => {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = url; });
    const f = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * f); canvas.height = Math.round(img.naturalHeight * f);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally { URL.revokeObjectURL(url); }
};

const rotateCanvas = (src, quarterTurns) => {
  const q = ((quarterTurns % 4) + 4) % 4;
  if (!q) return src;
  const c = document.createElement('canvas');
  c.width = q % 2 ? src.height : src.width; c.height = q % 2 ? src.width : src.height;
  const ctx = c.getContext('2d');
  ctx.translate(c.width / 2, c.height / 2); ctx.rotate(q * Math.PI / 2);
  ctx.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
};

// JPEG of the drawing, stepped down until it fits what the API accepts
const encodeScan = (canvas) => {
  let c = canvas, q = 0.8, out = c.toDataURL('image/jpeg', q);
  while (out.length > MAX_SCAN_CHARS && c.width > 800) {
    const d = document.createElement('canvas');
    d.width = Math.round(c.width * 0.8); d.height = Math.round(c.height * 0.8);
    d.getContext('2d').drawImage(c, 0, 0, d.width, d.height);
    c = d; q = Math.max(0.6, q - 0.05); out = c.toDataURL('image/jpeg', q);
  }
  return out;
};

const defaultForm = { name: '', client: '', site: '' };

const FloorPlans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === 'admin';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [drawing, setDrawing] = useState(null);   // { canvas, name, pdf, pages, page, wMm, hMm }
  const [scale, setScale] = useState('');
  const [turns, setTurns] = useState(0);
  const [backup, setBackup] = useState(null);     // parsed plan backup
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const previewRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/floorplans${showArchived ? '?archived=1' : ''}`);
      setPlans(res.data);
    } catch (err) {
      setError('Failed to load floor plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [showArchived]);

  useEffect(() => {
    const box = previewRef.current;
    if (!box) return;
    box.innerHTML = '';
    if (!drawing) return;
    const c = rotateCanvas(drawing.canvas, turns);
    c.style.cssText = 'max-width:100%;max-height:200px;display:block;margin:0 auto;border:1px solid var(--border);border-radius:4px';
    box.appendChild(c);
  }, [drawing, turns]);

  const openCreate = () => { setForm(defaultForm); setDrawing(null); setScale(''); setTurns(0); setBackup(null); setError(''); setModal(true); };

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleDrawing = async e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setBusy('Reading the drawing…');
    try {
      if (isPdf(file)) {
        const pdfjsLib = await loadPdfJs();
        const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
        const pg = await renderPdfPage(pdf, 1);
        setDrawing({ ...pg, name: file.name, pdf, pages: pdf.numPages, page: 1 });
      } else {
        setDrawing({ canvas: await imageToCanvas(file), name: file.name });
      }
      setTurns(0);
      if (!form.name) setForm(prev => ({ ...prev, name: file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ') }));
    } catch (err) {
      setError('Could not read that file. Use a PDF, JPEG or PNG of the drawing.');
    } finally { setBusy(''); }
  };

  const choosePage = async (n) => {
    if (!drawing?.pdf) return;
    setBusy(`Reading page ${n}…`); setError('');
    try {
      const pg = await renderPdfPage(drawing.pdf, n);
      setDrawing(prev => ({ ...prev, ...pg, page: n })); setTurns(0);
    } catch (err) {
      setError('Could not read that page.');
    } finally { setBusy(''); }
  };

  // With a PDF and its printed scale, the drawing's real width is known: paper width x scale
  const scaleNum = Number(scale);
  const trueWidth = () => {
    if (!drawing?.pdf || !(scaleNum > 0)) return null;
    const paperW = ((turns % 2) + 2) % 2 ? drawing.hMm : drawing.wMm;
    return paperW * scaleNum / 1000;
  };

  const handleBackup = e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const rd = new FileReader();
    rd.onload = () => {
      try {
        const p = JSON.parse(rd.result);
        if (!Array.isArray(p.walls)) throw new Error('not a plan');
        setBackup(p);
        setForm(prev => ({ name: prev.name || p.meta?.project || '', client: prev.client || p.meta?.client || '', site: prev.site || (p.meta?.site && !/NEED FROM ME/.test(p.meta.site) ? p.meta.site : '') }));
        setError('');
      } catch (err) { setError('That file is not a floor plan backup.'); }
    };
    rd.readAsText(file);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return setError('Give the plan a name');
    setBusy('Creating the plan…'); setError('');
    try {
      let scan = null, w = 0, h = 0;
      if (drawing) {
        const c = rotateCanvas(drawing.canvas, turns);
        scan = encodeScan(c); w = c.width; h = c.height;
      } else if (backup?.scanData && /^data:image\//.test(backup.scanData)) {
        scan = backup.scanData;
        const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = scan; });
        w = img.naturalWidth; h = img.naturalHeight;
      }
      const meta = { project: form.name.trim(), client: form.client.trim(), site: form.site.trim(), date: todayUK() };
      const startW = trueWidth() || ASSUMED_WIDTH_M;
      const newScan = () => ({ src: 'scan', x: 0, y: 0, w: r3(startW), h: r3(startW * h / w), opacity: 0.45, show: true });
      if (drawing?.pdf && scaleNum > 0) meta.note = `Drawn over ${drawing.name}, page ${drawing.page}, at 1:${scaleNum}.`;
      let data;
      if (backup) {
        const { scanData, ...rest } = backup; // eslint-disable-line no-unused-vars
        data = { ...rest, meta: { ...emptyPlan().meta, ...rest.meta, ...meta } };
        // a backup's own drawing keeps its position and scale; a newly uploaded drawing starts at the assumed width
        const keepGeometry = !drawing && rest.scan && rest.scan.w;
        data.scan = !scan ? null : keepGeometry ? { ...rest.scan, src: 'scan' } : newScan();
      } else {
        data = emptyPlan(meta, scan ? newScan() : null);
      }
      const res = await api.post('/api/floorplans', { name: meta.project, client: meta.client, site: meta.site, data, scan });
      setModal(false);
      navigate(`/floor-plans/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create the plan');
    } finally { setBusy(''); }
  };

  const toggleArchive = async (p) => {
    try {
      await api.put(`/api/floorplans/${p.id}/archive`, { archived: !p.archived });
      setSuccess(p.archived ? `${p.name} restored` : `${p.name} archived`);
      load();
    } catch (err) {
      setError('Could not update that plan');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Floor Plans</h1>
          <p className="page-subtitle">Drawings for refurbishments and extensions: walls to remove, new walls, doors and site notes</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => setShowArchived(!showArchived)}>{showArchived ? 'Show Current' : 'Show Archived'}</button>
          {canEdit && <button className="btn btn-primary" onClick={openCreate}>New Floor Plan</button>}
        </div>
      </div>

      {error && !modal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loader">Loading...</div>
      ) : plans.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>{showArchived ? 'No archived plans' : 'No floor plans yet'}</h3>
            <p>{canEdit && !showArchived ? 'Start one from a PDF or photo of the existing drawing.' : 'Plans appear here once an admin creates them.'}</p>
          </div>
        </div>
      ) : (
        <div className="fp-list">
          {plans.map(p => (
            <div className="card fp-card" key={p.id}>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ fontSize: '22px', lineHeight: 1.15 }}>{p.name}</h3>
                    <div style={{ fontSize: '13px', color: 'var(--grey-mid)', marginTop: '4px' }}>{[p.client, p.site].filter(Boolean).join(' · ') || 'No client set'}</div>
                  </div>
                  {p.has_scan && <span className="badge badge-holiday">Drawing</span>}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--grey-mid)', margin: '14px 0 16px' }}>
                  {p.wall_count} walls · {p.note_count} notes · updated {format(parseISO(p.updated_at), 'dd/MM/yyyy HH:mm')}{p.updated_by_name ? ` by ${p.updated_by_name}` : ''}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/floor-plans/${p.id}`)}>{canEdit ? 'Open' : 'View'}</button>
                  {canEdit && <button className="btn btn-outline btn-sm" onClick={() => toggleArchive(p)}>{p.archived ? 'Restore' : 'Archive'}</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => !busy && setModal(false)}>
          <div className="modal" style={{ maxWidth: '600px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New Floor Plan</h3>
              <button className="modal-close" onClick={() => !busy && setModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-group">
                <label className="form-label">Existing Drawing</label>
                <input type="file" accept="application/pdf,.pdf,image/jpeg,image/png,image/webp" onChange={handleDrawing} className="form-control" />
                <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Architect's or building regs PDF (any sheet), or a photo of a drawing. It sits underneath so you can trace over it. Optional.</p>
              </div>
              {drawing && (
                <div className="form-group">
                  <div ref={previewRef} />
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '8px' }}>
                    <button className="btn btn-outline btn-sm" onClick={() => setTurns(turns - 1)}>Rotate Left</button>
                    <button className="btn btn-outline btn-sm" onClick={() => setTurns(turns + 1)}>Rotate Right</button>
                  </div>
                </div>
              )}
              {drawing?.pdf && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Sheet</label>
                    <select className="form-control" value={drawing.page} onChange={e => choosePage(Number(e.target.value))} disabled={!!busy || drawing.pages < 2}>
                      {Array.from({ length: drawing.pages }, (_, i) => <option key={i + 1} value={i + 1}>Page {i + 1} of {drawing.pages}</option>)}
                    </select>
                    <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>{paperName(drawing.wMm, drawing.hMm)}</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Drawing Scale</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '14px' }}>1:</span>
                      <input className="form-control" list="fp-scales" inputMode="numeric" value={scale} onChange={e => setScale(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="e.g. 50" />
                      <datalist id="fp-scales">{SCALES.map(v => <option key={v} value={v} />)}</datalist>
                    </div>
                    <p style={{ fontSize: '11px', color: trueWidth() ? 'var(--success)' : 'var(--grey-mid)', marginTop: '4px' }}>
                      {trueWidth() ? `Sheet is ${trueWidth().toFixed(1)} m wide at this scale` : 'As printed on the sheet, at its own paper size'}
                    </p>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Plan Name</label>
                <input name="name" className="form-control" value={form.name} onChange={handleChange} placeholder="e.g. NMS office refurbishment" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <input name="client" className="form-control" value={form.client} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Site</label>
                  <input name="site" className="form-control" value={form.site} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Start From a Backup (optional)</label>
                <input type="file" accept=".json,application/json" onChange={handleBackup} className="form-control" />
                <p style={{ fontSize: '11px', color: backup ? 'var(--success)' : 'var(--grey-mid)', marginTop: '4px' }}>
                  {backup ? `Backup loaded: ${backup.walls.length} walls, ${(backup.notes || []).length} notes.` : 'A floor plan backup file (.json) brings its walls, doors, labels and notes across.'}
                </p>
              </div>
              <div className="alert alert-info" style={{ marginTop: '16px', marginBottom: 0 }}>
                {drawing?.pdf && trueWidth()
                  ? 'The drawing will open to scale. Check one dimension with the measure tool before relying on it.'
                  : 'Once it opens, use the measure tool on a length you know to set the scale.'}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(false)} disabled={!!busy}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={!!busy}>{busy || 'Create Plan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlans;
